'use client';

import { type ReactElement, useReducer } from 'react';
import { Assign, Comment, Filter, Hash, Link, Padlock, Share, Workspace } from '@doist/icons/brand';
import { Box, Button, Quote } from '@doist/marketist';
import { FeatureRadioGroup } from 'components/feature-radio-tile/feature-radio-group';
import { RadioTileScene, SceneWrapper } from 'components/feature-radio-tile/scene-images';
import { AdvancedPermissionsScene } from 'components/product-ui/scenes/advanced-permissions';
import { AssignedTasksScene } from 'components/product-ui/scenes/assigned-tasks';
import { CommentsScene } from 'components/product-ui/scenes/comments';
import { FilterByTeamOrPersonalProjectScene } from 'components/product-ui/scenes/filter-by-team-or-personal-projects';
import { ProjectSharingScene } from 'components/product-ui/scenes/project-sharing';
import { PublicPrivateTeamProjectsScene } from 'components/product-ui/scenes/public-private-team-projects';
import { SharedProjectsScene } from 'components/product-ui/scenes/shared-projects';
import { WorkspacesScene } from 'components/product-ui/scenes/workspaces';
import { ScrollHeader } from 'components/scrolling-section/scroll-header/scroll-header';
import { ScrollSection } from 'components/scrolling-section/scroll-section/scroll-section';
import { useTranslation } from 'next-i18next';
import { useLocalisedContent } from 'services/hooks/use-localised-data';
import { quoteRamon } from './quote-map';
import css from './feature-teams.module.css';
const radioTiles = ['sharedProjects', 'assignedTasks', 'comments'] as const;
const teamTiles = ['sharedWorkspace', 'projectSharingLink', 'publicPrivateProjects', 'filterByTeamOrProject', 'rolesPermissions'] as const;
type RadioTile = (typeof radioTiles)[number];
type TeamTile = (typeof teamTiles)[number];
type AllTile = RadioTile | TeamTile;
type RadioGroupAction = {
  type: 'updateRadio';
  value?: RadioTile;
} | {
  type: 'updateTeam';
  value?: TeamTile;
};
type GroupState = {
  activeRadioTile: RadioTile;
  activeTeamTile: TeamTile;
  currentTile: AllTile;
};
const initialState: GroupState = {
  activeRadioTile: 'sharedProjects',
  activeTeamTile: 'sharedWorkspace',
  currentTile: 'sharedProjects'
};
function radioGroupReducer(state: GroupState, action: RadioGroupAction): GroupState {
  switch (action.type) {
    case 'updateRadio':
      return {
        ...state,
        activeRadioTile: action.value || state.activeRadioTile,
        currentTile: action.value || state.activeRadioTile
      };
    case 'updateTeam':
      return {
        ...state,
        activeTeamTile: action.value || state.activeTeamTile,
        currentTile: action.value || state.activeTeamTile
      };
    default:
      return state;
  }
}
export function CollaborationMadeEasy() {
  const {
    t
  } = useTranslation('features-teams');
  const avatarSrc = useLocalisedContent(quoteRamon);
  const [state, dispatch] = useReducer(radioGroupReducer, initialState);
  function radioTileChangeHandler(value: RadioTile) {
    dispatch({
      type: 'updateRadio',
      value
    });
  }
  function teamTileChangeHandler(value: TeamTile) {
    dispatch({
      type: 'updateTeam',
      value
    });
  }
  const sceneImages: { [key in AllTile]: string | ReactElement } = {
    sharedProjects: <SharedProjectsScene />,
    assignedTasks: <AssignedTasksScene />,
    comments: <CommentsScene />,
    sharedWorkspace: <WorkspacesScene />,
    publicPrivateProjects: <PublicPrivateTeamProjectsScene />,
    projectSharingLink: <ProjectSharingScene />,
    filterByTeamOrProject: <FilterByTeamOrPersonalProjectScene />,
    rolesPermissions: <AdvancedPermissionsScene />
  };
  return <ScrollSection data-sentry-element="ScrollSection" data-sentry-component="CollaborationMadeEasy" data-sentry-source-file="collaboration-made-easy.tsx">
            <ScrollSection.StaticSection data-sentry-element="unknown" data-sentry-source-file="collaboration-made-easy.tsx">
                <ScrollHeader title={t('collaboration')} spotIllustration={{
        src: '/static/features-teams/features_spot4.png',
        offset: {
          x: -94,
          y: -63
        },
        mobileOffset: {
          x: -36,
          y: -40
        }
      }} data-sentry-element="ScrollHeader" data-sentry-source-file="collaboration-made-easy.tsx">
                    {t('keepEveryoneSync')}
                </ScrollHeader>

                <FeatureRadioGroup<RadioTile> onChange={radioTileChangeHandler} value={state.activeRadioTile} namespace="features-teams" radioTiles={[{
        titleKey: 'sharedProjects',
        bodyKey: 'sharedProjectsBody',
        icon: <Link />,
        scene: <RadioTileScene scene={sceneImages.sharedProjects} />
      }, {
        titleKey: 'assignedTasks',
        bodyKey: 'assignedTasksBody',
        icon: <Assign />,
        scene: <RadioTileScene scene={sceneImages.assignedTasks} />
      }, {
        titleKey: 'comments',
        bodyKey: 'commentsBody',
        icon: <Comment />,
        scene: <RadioTileScene scene={sceneImages.comments} />
      }]} data-sentry-element="FeatureRadioGroup" data-sentry-source-file="collaboration-made-easy.tsx" />

                <Quote quoteText={t('quoteRamon.quote')} author={t('quoteRamon.name')} role={t('quoteRamon.role')} avatarSrc={`/static/features-teams/avatars/${avatarSrc}`} className="mb-96 md-mb-192" data-sentry-element="Quote" data-sentry-source-file="collaboration-made-easy.tsx" />

                <Box className={css.workspaceSection} data-sentry-element="Box" data-sentry-source-file="collaboration-made-easy.tsx">
                    <ScrollSection.Trigger onEnter={() => {
          dispatch({
            type: 'updateTeam'
          });
        }} onEnterBack={() => {
          dispatch({
            type: 'updateRadio'
          });
        }} data-sentry-element="unknown" data-sentry-source-file="collaboration-made-easy.tsx">
                        <ScrollHeader title={t('homeTeam')} spotIllustration={{
            src: '/static/features-teams/features_spot5.png',
            offset: {
              x: -112,
              y: -36
            },
            mobileOffset: {
              x: -36,
              y: -41
            }
          }} data-sentry-element="ScrollHeader" data-sentry-source-file="collaboration-made-easy.tsx">
                            {t('techConstruction')}
                        </ScrollHeader>
                    </ScrollSection.Trigger>

                    <Box className={css.radioGroupWrapper} data-sentry-element="Box" data-sentry-source-file="collaboration-made-easy.tsx">
                        <FeatureRadioGroup<TeamTile> onChange={teamTileChangeHandler} value={state.activeTeamTile} namespace="features-teams" radioTiles={[{
            titleKey: 'sharedWorkspace',
            bodyKey: 'sharedWorkspaceBody',
            icon: <Workspace />,
            scene: <RadioTileScene scene={sceneImages.sharedWorkspace} />
          }, {
            titleKey: 'publicPrivateProjects',
            bodyKey: 'publicPrivateProjectsBody',
            copySpacing: false,
            icon: <Hash />,
            scene: <RadioTileScene scene={sceneImages.publicPrivateProjects} />
          }, {
            titleKey: 'projectSharingLink',
            bodyKey: 'projectSharingLinkBody',
            copySpacing: false,
            icon: <Share />,
            scene: <RadioTileScene scene={sceneImages.projectSharingLink} />
          }, {
            titleKey: 'filterByTeamOrProject',
            bodyKey: 'filterByTeamOrProjectBody',
            copySpacing: false,
            icon: <Filter />,
            scene: <RadioTileScene scene={sceneImages.filterByTeamOrProject} />
          }, {
            titleKey: 'rolesPermissions',
            bodyKey: 'rolesPermissionsBody',
            copySpacing: false,
            icon: <Padlock />,
            scene: <RadioTileScene scene={sceneImages.rolesPermissions} />
          }]} data-sentry-element="FeatureRadioGroup" data-sentry-source-file="collaboration-made-easy.tsx" />

                        <Button variant="tertiary" href="/teams" size="large" data-sentry-element="Button" data-sentry-source-file="collaboration-made-easy.tsx">
                            {t('learnMoreTeamwork')}
                        </Button>
                    </Box>
                </Box>

                <ScrollSection.StickyAnchor data-sentry-element="unknown" data-sentry-source-file="collaboration-made-easy.tsx">
                    <Quote quoteText={t('quoteLeon.quote')} author={t('quoteLeon.name')} role={t('quoteLeon.role')} avatarSrc="/static/features-teams/avatars/leon-s.png" className="md-mbs-192 mbs-96" data-sentry-element="Quote" data-sentry-source-file="collaboration-made-easy.tsx" />
                </ScrollSection.StickyAnchor>
            </ScrollSection.StaticSection>
            <ScrollSection.StickySection data-sentry-element="unknown" data-sentry-source-file="collaboration-made-easy.tsx">
                <SceneWrapper scene={sceneImages[state.currentTile]} data-sentry-element="SceneWrapper" data-sentry-source-file="collaboration-made-easy.tsx" />
            </ScrollSection.StickySection>
        </ScrollSection>;
}